import {
  ResumeColorMode,
  ResumeDocument,
  ResumeFontFamilyId,
  ResumePersonalization,
} from '../../types';

interface FontStack {
  html: string; // CSS font-family value
  docx: string; // Word font name (must exist on the target machine)
}

// Curated cross-platform stacks. The HTML side relies on each platform's
// shipping system fonts (no @font-face loading inside expo-print). The DOCX
// side picks a font that ships with Office and most modern OSs so the file
// looks consistent when opened in Word.
export const FONT_FAMILIES: Record<ResumeFontFamilyId, FontStack> = {
  sans: {
    html: '-apple-system, "Helvetica Neue", "Segoe UI", Roboto, Arial, sans-serif',
    docx: 'Calibri',
  },
  serif: {
    html: 'Georgia, "Times New Roman", "Liberation Serif", serif',
    docx: 'Georgia',
  },
  modern: {
    html: '"Avenir Next", Avenir, "Trebuchet MS", system-ui, sans-serif',
    docx: 'Avenir',
  },
  mono: {
    html: 'Menlo, Consolas, "Liberation Mono", "Courier New", monospace',
    docx: 'Consolas',
  },
};

export interface ResolvedTypography {
  colorMode: ResumeColorMode;
  fontFamilyHtml: string;
  fontFamilyDocx: string;
  scale: { title: number; subtitle: number; body: number };
}

export const resolvePersonalization = (
  p: ResumePersonalization
): ResolvedTypography => {
  const fam = FONT_FAMILIES[p.fontFamily] ?? FONT_FAMILIES.sans;
  return {
    colorMode: p.colorMode,
    fontFamilyHtml: fam.html,
    fontFamilyDocx: fam.docx,
    scale: {
      title: p.fontScale?.title ?? 1,
      subtitle: p.fontScale?.subtitle ?? 1,
      body: p.fontScale?.body ?? 1,
    },
  };
};

// Round to integer half-points for DOCX so Word doesn't render half-pixels.
export const scaledHalfPoints = (basePt: number, scale: number) =>
  Math.max(8, Math.round(basePt * 2 * scale));

// HTML font-size string (px) honouring the user's scale.
export const scaledPx = (basePx: number, scale: number) =>
  `${Math.max(8, Math.round(basePx * scale * 100) / 100)}px`;

// Split a "data:image/...;base64,..." URI into the bits the docx library
// needs (type tag + raw base64). Returns null when the URI is empty or in
// an unsupported format so callers can short-circuit.
export const parseImageDataUri = (
  dataUri: string
): { type: 'jpg' | 'png'; base64: string } | null => {
  if (!dataUri) return null;
  const m = dataUri.match(/^data:image\/(jpe?g|png);base64,(.+)$/i);
  if (!m) return null;
  const ext: 'jpg' | 'png' = m[1].toLowerCase() === 'png' ? 'png' : 'jpg';
  return { type: ext, base64: m[2] };
};

export const escapeHtml = (s: string): string =>
  (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const nl2br = (s: string): string =>
  escapeHtml(s ?? '').replace(/\r?\n/g, '<br/>');

export const formatPhone = (countryCode: string, phone: string): string => {
  const cc = (countryCode ?? '').trim();
  const num = (phone ?? '').trim();
  if (!cc && !num) return '';
  if (!cc) return num;
  const prefix = cc.startsWith('+') ? cc : `+${cc}`;
  return num ? `${prefix} ${num}` : prefix;
};

export const formatPeriod = (
  start: string,
  end: string,
  current: boolean,
  presentLabel: string
): string => {
  const fmt = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${m}/${y}`;
  };
  const s = fmt(start);
  const e = current ? presentLabel : fmt(end);
  if (s && e) return `${s} – ${e}`;
  return s || e;
};

export const hasAnyContent = (resume: ResumeDocument): boolean =>
  Boolean(
    resume.personalInfo.fullName ||
      resume.personalInfo.summary ||
      resume.experience.length ||
      resume.education.length ||
      resume.skills.length ||
      resume.languages.length ||
      resume.links.length
  );

export default {
  escapeHtml,
  nl2br,
  formatPhone,
  formatPeriod,
  hasAnyContent,
  FONT_FAMILIES,
  resolvePersonalization,
  scaledHalfPoints,
  scaledPx,
  parseImageDataUri,
};
